import React from "react"

import Icon from "../../../Icon"
import { hexToRgba } from "../../../utils/colors.ts"
import { ENTITY_GLYPH, ENTITY_ICON_COLOR } from "./entity-icon-colors.ts"
import type { MapLayer } from "../redux/global-map.types"

// Shared with `GlobalMapHeader`'s KPI tiles — a soft wash of the entity's own
// colour behind a glyph drawn in that same colour, rather than white reversed
// out of a saturated block.
const TILE_TINT_ALPHA = 0.12

interface EntityGlyphTileProps {
  entityType: MapLayer
  // Square edge in px. The tile scales its own glyph and corner radius from
  // this, so a caller only picks one number.
  size?: number
  className?: string
}

/**
 * A record's entity badge: the tinted rounded tile the approved design uses
 * for the quick-view header and the Nearby rows.
 *
 * Deliberately separate from `EntityIcon`, which reverses a white glyph out
 * of a solid colour — that treatment is still correct for the map marker and
 * the dense Recent Activity rows, where the badge has to read against a map
 * tile or at 16px. This is the larger, lighter treatment used wherever the
 * badge sits on a white panel beside a record name.
 */
export function EntityGlyphTile({
  entityType,
  size = 40,
  className
}: EntityGlyphTileProps) {
  const color = ENTITY_ICON_COLOR[entityType]
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center ${className ?? ""}`}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.25),
        backgroundColor: hexToRgba(color, TILE_TINT_ALPHA),
        color,
        fontSize: Math.round(size * 0.45)
      }}
    >
      <Icon type={ENTITY_GLYPH[entityType]} />
    </span>
  )
}

export default EntityGlyphTile
